import { useQuery } from '@tanstack/react-query';
import { Activity, ArrowLeft, BookOpen, Cpu, HardDrive, History, Info, MemoryStick, SquareTerminal } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart';
import { MetricRow, Pill, SectionCard, StatusPill } from '@/components/panels/SectionCard';
import {
  formatBytes,
  formatRate,
  formatUptime,
  metricsApi,
  serverMetricsKey,
} from '@/lib/metrics-api';
import { serversApi } from '@/lib/servers-api';
import { hardwareSummary, privilegeBadge, privilegeProblem } from '@/modules/servers/describe';
import { TrustHostKey } from '@/modules/servers/TrustHostKey';
import type { MetricPoint, Server } from '@/types/api';

/** The windows offered. Bounded by the 30-day retention horizon on the server. */
const WINDOWS = [
  { hours: 1, label: '1H' },
  { hours: 24, label: '24H' },
  { hours: 168, label: '7D' },
  { hours: 720, label: '30D' },
] as const;

/**
 * One server: what it is, what it is doing now, and what it has been doing.
 *
 * ## What it is comes first
 *
 * Distribution, hardware and how the platform reaches root are what decide whether a playbook can
 * run here, so they head the page with the actions that use them — run a command or a playbook on
 * this server, or read every run that has touched it. All of it is what the last connection check
 * found; nothing here contacts the server.
 *
 * ## The page refreshes on the collection interval, not faster
 *
 * Samples arrive every five minutes. Polling every ten seconds would issue thirty requests to
 * re-render the same data — the chart cannot show anything the collector has not gathered.
 *
 * ## Every gap stays a gap
 *
 * Nulls travel from the database through the API to the chart untouched, and the chart breaks its
 * line. See `TimeSeriesChart` — a period the platform did not observe must not be drawn as though
 * it were.
 */
export function ServerDetailPage() {
  const { id = '' } = useParams();
  const [hours, setHours] = useState<number>(1);

  const metrics = useQuery({
    queryKey: serverMetricsKey(id, hours),
    queryFn: () => metricsApi.server(id, hours),
    enabled: id !== '',
    // Matches the collector. Anything faster is re-fetching data that has not changed.
    refetchInterval: 300_000,
  });

  // The inventory the rest of the app already holds, rather than a request of its own.
  const servers = useQuery({ queryKey: ['servers', 'all'], queryFn: () => serversApi.all(), staleTime: 30_000 });
  const server = servers.data?.find((item) => item.id === id);

  const data = metrics.data;
  const latest = data?.latest ?? null;
  const points = data?.points ?? [];

  const series = (pick: (p: MetricPoint) => number | null) =>
    points.map((p) => ({ at: p.at, value: pick(p) }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to="/servers"
            className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3" />
            Servers
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            {data?.server.name ?? server?.name ?? 'Server'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {data?.server.endpoint}
            {data?.server.os !== undefined && data.server.os !== '' && ` · ${data.server.os}`}
            {latest !== null && latest.uptime_seconds !== null && latest.uptime_seconds > 0 && ` · up ${formatUptime(latest.uptime_seconds)}`}
          </p>
        </div>

        {data !== undefined && (
          <div className="ml-auto">
            <StatusPill
              label={data.server.ready ? 'Online' : 'Unverified'}
              tone={data.server.ready ? 'success' : 'warning'}
            />
          </div>
        )}
      </div>

      {server !== undefined && <SystemCard server={server} />}

      {metrics.isSuccess && latest === null && (
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          This server has not reported yet. The platform samples every five minutes, so the first
          reading appears shortly after it is added. And the CPU and network figures need a second
          reading before they can be worked out at all.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <SectionCard
          title="Resource Usage"
          subtitle="Live infrastructure utilisation"
          action={<Activity className="size-5 text-primary" />}
          className="space-y-6"
        >
          <div className="space-y-6">
            <MetricRow
              icon={Cpu}
              colour="text-blue-400"
              strokeColour="rgb(96 165 250)"
              label="CPU Usage"
              description={latest?.cpu_cores ? `Average across ${latest.cpu_cores} cores` : 'Average across all cores'}
              value={latest?.cpu_percent ?? null}
              series={points.map((p) => p.cpu_percent)}
              max={100}
            />
            <MetricRow
              icon={MemoryStick}
              colour="text-purple-400"
              strokeColour="rgb(192 132 252)"
              label="Memory Usage"
              description={
                latest === null
                  ? 'Average RAM utilisation'
                  : `${formatBytes(latest.memory_used_bytes)} of ${formatBytes(latest.memory_total_bytes)}`
              }
              value={latest?.memory_percent ?? null}
              series={points.map((p) => p.memory_percent)}
              max={100}
            />
            <MetricRow
              icon={HardDrive}
              colour="text-cyan-400"
              strokeColour="rgb(34 211 238)"
              label="Disk Usage"
              description={
                latest === null
                  ? 'Root filesystem'
                  : `${formatBytes(latest.disk_used_bytes)} of ${formatBytes(latest.disk_total_bytes)} on /`
              }
              value={latest?.disk_percent ?? null}
              series={points.map((p) => p.disk_percent)}
              max={100}
            />
          </div>

          {latest !== null && (
            <div className="mt-6 grid grid-cols-2 gap-4 border-t pt-4 text-sm">
              <div>
                <p className="text-muted-foreground">Load per core</p>
                <p className="font-semibold tabular-nums">{latest.load_per_core}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Load average (1m)</p>
                <p className="font-semibold tabular-nums">{latest.load_1}</p>
              </div>
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Network Traffic"
          subtitle="Throughput, excluding loopback and container bridges"
          action={
            <div className="flex gap-1">
              {WINDOWS.map((window) => (
                <button
                  key={window.hours}
                  type="button"
                  onClick={() => setHours(window.hours)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    hours === window.hours
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-transparent text-muted-foreground hover:bg-muted/60'
                  }`}
                >
                  {window.label}
                </button>
              ))}
            </div>
          }
        >
          <TimeSeriesChart
            series={[
              { label: 'In', color: 'rgb(96 165 250)', points: series((p) => p.rx_bytes_per_sec) },
              { label: 'Out', color: 'rgb(52 211 153)', points: series((p) => p.tx_bytes_per_sec) },
            ]}
            formatValue={(v) => formatRate(Math.round(v)).replace('/s', '')}
            emptyMessage="No network readings for this period. Rates need two samples five minutes apart."
          />
        </SectionCard>
      </div>

      <SectionCard
        title="Resource history"
        subtitle={`Last ${hours < 24 ? `${hours} hour${hours === 1 ? '' : 's'}` : `${Math.round(hours / 24)} days`} · kept for ${data?.retention_days ?? 30} days`}
        action={<Pill>{points.length} readings</Pill>}
      >
        <TimeSeriesChart
          max={100}
          formatValue={(v) => `${Math.round(v)}%`}
          series={[
            { label: 'CPU', color: 'rgb(96 165 250)', points: series((p) => p.cpu_percent) },
            { label: 'Memory', color: 'rgb(192 132 252)', points: series((p) => p.memory_percent) },
            { label: 'Disk', color: 'rgb(34 211 238)', points: series((p) => p.disk_percent) },
          ]}
          emptyMessage="No readings for this period yet."
        />
      </SectionCard>
    </div>
  );
}

/** What the last connection check found, and the three things to do with this server. */
function SystemCard({ server }: { server: Server }) {
  const privilege = privilegeBadge(server);
  const problem = privilegeProblem(server);
  const hardware = hardwareSummary(server);
  const facts = server.facts;

  return (
    <SectionCard
      title="System"
      subtitle={
        server.last_checked_at === null
          ? 'Not checked yet. Test the connection from the servers list.'
          : `As found by the last connection check, ${new Date(server.last_checked_at).toLocaleString()}`
      }
      action={
        <div className="flex flex-wrap gap-2">
          <ActionLink to={`/automation/commands?server=${server.id}`} icon={SquareTerminal}>
            Run a command
          </ActionLink>
          <ActionLink to={`/automation/playbooks?server=${server.id}`} icon={BookOpen}>
            Run a playbook
          </ActionLink>
          <ActionLink to={`/automation/runs?server_id=${server.id}`} icon={History}>
            Runs
          </ActionLink>
        </div>
      }
    >
      <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Operating system">
          {server.os_label ?? 'Not detected yet'}
          {server.architecture_label !== null && ` · ${server.architecture_label}`}
        </Fact>
        <Fact label="Hostname">
          {server.hostname !== '' ? <span className="font-mono text-xs">{server.hostname}</span> : 'Not detected yet'}
        </Fact>
        <Fact label="Package manager">{server.package_manager ?? 'Not detected yet'}</Fact>
        <Fact label="Service manager">{facts?.init_system_label ?? 'Not detected yet'}</Fact>
        <Fact label="Hardware" title={hardware.join(' · ')}>
          {hardware.length > 0 ? hardware.join(' · ') : 'Not detected yet'}
        </Fact>
        <Fact label="Kernel" title={facts?.kernel ?? undefined}>
          {facts?.kernel ? <span className="font-mono text-xs">{facts.kernel}</span> : 'Not detected yet'}
        </Fact>
        <Fact label="Signed in as">
          <span className="font-mono text-xs">{facts?.connection_user ?? server.ssh_user}</span>
        </Fact>
        <Fact label="Root access">
          <span
            className={
              privilege.tone === 'success' ? 'text-success' : privilege.tone === 'warning' ? 'text-warning' : 'text-muted-foreground'
            }
          >
            {privilege.label}
          </span>
        </Fact>
        <Fact label="Connection">
          {server.status_label}
          {server.host_key_fingerprint !== null && (
            <span className="block truncate font-mono text-[11px] text-muted-foreground" title={server.host_key_fingerprint}>
              {server.host_key_fingerprint}
            </span>
          )}
        </Fact>
      </dl>

      {(problem !== null || (server.os_label !== null && !server.os_supported) || server.last_error !== null) && (
        <div className="mt-4 space-y-2 border-t pt-4 text-sm">
          {server.os_label !== null && !server.os_supported && (
            <p className="flex items-start gap-2 text-warning">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
              Playbooks and templates are not written for this distribution. Commands still run.
            </p>
          )}
          {problem !== null && (
            <p className="flex items-start gap-2 text-warning">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
              {problem}
            </p>
          )}
          {server.last_error !== null && (
            <p className="flex items-start gap-2 text-danger">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
              {server.last_error}
            </p>
          )}
        </div>
      )}

      {/* Outside the error block: once the key is trusted the error goes, and what happened must
          still be said where the button was. */}
      <TrustHostKey server={server} className="mt-3 pl-6" />
    </SectionCard>
  );
}

/** One fact. Cut off with an ellipsis when the column is narrow, so `title` carries the whole value. */
function Fact({ label, title, children }: { label: string; title?: string | undefined; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate" title={title === '' ? undefined : title}>
        {children}
      </dd>
    </div>
  );
}

function ActionLink({ to, icon: Icon, children }: { to: string; icon: typeof History; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium transition-colors hover:bg-muted/50"
    >
      <Icon className="size-3.5" aria-hidden />
      {children}
    </Link>
  );
}
